import { useCurrentGroupContext } from "hooks/useCurrentGroupContext";
import { useToast } from "hooks/useToast";
import { FC } from "react";
import {
  useUpdateMerchantFormMutation,
  useUpdateTaskMerchantMutation,
} from "store/apis/taskMerchants";
import { useGetTeamMembersQuery } from "store/apis/teamSetting";
import { Merchant } from "types/Models/merchant";
import * as Yup from "yup";
import { Button } from "./DesignSystem/Button/Button";
import { Combobox } from "./DesignSystem/Combobox/Combobox";
import Modal from "./DesignSystem/Modal/Modal";
import { TextInput } from "./DesignSystem/TextInput/TextInput";
import { FormikForm } from "./FormikForm/FormikForm";
import { WFormEmailPreview } from "./WFormEmailPreview";

const validationSchema = Yup.object({
  email: Yup.string()
    .required("Email is required")
    .email("Please enter a valid email"),
  cc_emails: Yup.array()
    .of(Yup.string())
    .min(1, "Please select at least one CC email")
    .required("CC email is required"),
});
type IWSeriesRequestFormProps = {
  show: boolean;
  closeModal: () => void;
  taskId: string;
  groupId: string;
  merchant: Merchant;
  updateMerchant: () => void;
  season: string;
};

export const WSeriesRequestForm: FC<IWSeriesRequestFormProps> = ({
  show,
  closeModal,
  taskId,
  groupId,
  merchant,
  updateMerchant,
  season,
}) => {
  const group = useCurrentGroupContext();
  const { form_status, email, uuid } = merchant || {};
  const [updateTaskMerchant] = useUpdateTaskMerchantMutation();
  const [updateMerchantForm] = useUpdateMerchantFormMutation();
  const { alertToast, successToast } = useToast();
  const { data: teamMembers, isLoading: isLoadingTeamMembers } =
    useGetTeamMembersQuery({ groupId }, { skip: !groupId });

  const allTeamEmails =
    teamMembers?.group_users
      ?.filter(
        ({ is_archived_user, is_verified }) => !is_archived_user && is_verified
      )
      ?.map(({ email }) => email) || [];

  const ccEmailOptions = allTeamEmails.map((email) => ({
    label: email,
    value: email,
  }));

  const onEmailSubmit = async ({
    email,
    cc_emails,
  }: {
    email: string;
    cc_emails: string[];
  }) => {
    try {
      const ccEmailsString =
        cc_emails.length > 0 ? cc_emails.join(",") : undefined;

      if (taskId) {
        await updateTaskMerchant({
          taskId,
          groupId,
          merchantId: uuid,
          email,
          season,
          cc_emails: ccEmailsString,
        });
        updateMerchant();
      } else {
        await updateMerchantForm({
          groupId,
          merchantId: uuid,
          email,
          season,
          cc_emails: ccEmailsString,
        });
        updateMerchant();
      }
      closeModal();
      successToast({ message: "Email has been sent" });
    } catch (error: any) {
      alertToast({ message: error?.response?.data?.error?.message });
    }
  };
  return (
    <Modal.Root open={show} onOpenChange={closeModal}>
      <Modal.Portal>
        <Modal.Overlay />
        <Modal.Content size="large" asChild>
          <FormikForm
            onSubmit={onEmailSubmit}
            initialValues={{ email, cc_emails: allTeamEmails }}
            validationSchema={validationSchema}
            enableReinitialize
          >
            {({ isSubmitting, values }) => (
              <>
                <Modal.Header>
                  <Modal.Title>Request W-Form</Modal.Title>
                  <Modal.Close />
                </Modal.Header>
                <Modal.Body className="t-flex t-flex-col t-gap-4">
                  <p className="t-m-0 t-text-body-sm t-text-text-60">
                    Please enter the vendor's email address to request the
                    W-Form. An email will be sent to the vendor with the
                    following message.
                  </p>

                  <TextInput
                    label="To"
                    block
                    placeholder="Enter email of the contractor"
                    name="email"
                    required
                    customSize="regular"
                  />

                  <Combobox
                    name="cc_emails"
                    label="CC"
                    size="regular"
                    isMulti
                    withForm
                    placeholder="Add CC email addresses"
                    options={ccEmailOptions}
                    value={values.cc_emails.map((email: string) => ({
                      label: email,
                      value: email,
                    }))}
                  />

                  <div>
                    <div className="t-mb-3 t-flex t-items-center t-justify-center t-h-full t-gap-3">
                      <div className="t-w-full t-bg-purple-divider-gradient t-h-[1px] t-rotate-180" />
                      <div className="t-text-nowrap t-text-subtext-sm t-text-text-60">
                        Email Preview
                      </div>
                      <div className="t-w-full t-bg-purple-divider-gradient t-h-[1px]" />
                    </div>

                    <WFormEmailPreview groupName={group.name} season={season} />
                  </div>
                </Modal.Body>

                <Modal.FooterButtonGroup>
                  <Modal.RawClose asChild>
                    <Button>Cancel</Button>
                  </Modal.RawClose>
                  <Button
                    customType="primary"
                    isLoading={isSubmitting || isLoadingTeamMembers}
                    disabled={isSubmitting || isLoadingTeamMembers}
                  >
                    {form_status === "SENT" ? "Resend email" : "Send email"}
                  </Button>
                </Modal.FooterButtonGroup>
              </>
            )}
          </FormikForm>
        </Modal.Content>
      </Modal.Portal>
    </Modal.Root>
  );
};
